import { useRef, useState } from 'react';
import { BookOpen, Check, ChevronDown, ChevronUp, Music2, Play, RotateCcw, X } from 'lucide-react';
import type { RoadmapLevelStage, RoadmapPiece } from 'Selectors/curriculum';

export type RoadmapSelection =
  | { kind: 'lesson'; levelNumber: number }
  | { kind: 'stage'; levelNumber: number }
  | { kind: 'piece'; levelNumber: number; pieceId: number };

const previewUrl = (src?: string) => {
  if (!src) return '';
  return /^(https?:|data:|\/)/.test(src) ? src : `https://${src}`;
};

function ScorePreview({ src, title }: { src: string; title: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="rm-score-preview">
      <span>PREVIEW</span>
      {src && !failed ? (
        <img src={src} alt={`Music preview for ${title}`} onError={() => setFailed(true)} />
      ) : (
        <div className="rm-no-preview">
          <Music2 size={28} />
          <small>Preview unavailable</small>
        </div>
      )}
    </div>
  );
}

export function RoadmapDetails({
  stage,
  selection,
  piece,
  onPlay,
  onReplay,
  onBrowse,
  onClose,
  mobile,
  expanded,
  onExpand,
}: {
  stage: RoadmapLevelStage;
  selection: RoadmapSelection;
  piece?: RoadmapPiece;
  onPlay: () => void;
  onReplay: () => void;
  onBrowse: () => void;
  onClose: () => void;
  mobile: boolean;
  expanded: boolean;
  onExpand: (value: boolean) => void;
}) {
  const dragY = useRef<number | null>(null);
  const isLesson = selection.kind === 'lesson';
  const isPiece = selection.kind === 'piece' && piece;
  const branch = stage.repertoire;
  const title = isLesson ? stage.level.name : isPiece ? piece.piece.name : 'Repertoire';
  const subtitle = isLesson
    ? stage.levelNumber === 0
      ? 'YOUR FIRST STEPS'
      : `LEVEL ${stage.levelNumber}`
    : isPiece
      ? `LEVEL ${stage.levelNumber} · REPERTOIRE`
      : `FROM LEVEL ${stage.levelNumber}`;
  const description = isLesson
    ? stage.level.description
    : isPiece
      ? piece.piece.composers.map((c) => c.name).join(', ') ||
        piece.piece.artists.map((a) => a.name).join(', ')
      : branch?.availability === 'future'
        ? 'A little further along your journey. You can explore these pieces anytime.'
        : 'Bring what you’ve learned to life. Find your next piece on this stage.';
  const src = isLesson
    ? stage.levelNumber
      ? previewUrl(stage.level.srcs)
      : ''
    : previewUrl((isPiece ? piece : branch?.pieces[0])?.piece.repertoire_png_preview_url);
  const progressText = isLesson
    ? stage.levelNumber === 0
      ? 'An introduction to MuseFlow'
      : `${Math.min(stage.highestTierComplete, stage.tierCount)} of ${stage.tierCount} tiers complete`
    : isPiece
      ? `${Math.floor(piece.percentComplete)}% complete`
      : `${branch?.completedCount ?? 0} of ${branch?.pieces.length ?? 0} pieces complete`;
  const progress = isLesson
    ? stage.tierCount
      ? Math.min(100, (stage.highestTierComplete / stage.tierCount) * 100)
      : stage.lessonState === 'complete'
        ? 100
        : 0
    : isPiece
      ? piece.percentComplete
      : (branch?.percentComplete ?? 0);
  return (
    <section
      className={`rm-details ${mobile ? 'is-sheet' : ''} ${expanded ? 'is-expanded' : ''}`}
      data-testid="roadmap-details"
      aria-label={`${title} details`}
    >
      {mobile && (
        <div
          className="rm-sheet-handle"
          onPointerDown={(e) => {
            if ((e.target as Element).closest('button')) return;
            dragY.current = e.clientY;
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerUp={(e) => {
            if (dragY.current !== null && Math.abs(e.clientY - dragY.current) > 30)
              onExpand(e.clientY < dragY.current);
            dragY.current = null;
          }}
          onPointerCancel={() => {
            dragY.current = null;
          }}
        >
          <button
            type="button"
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            aria-expanded={expanded}
            onClick={() => onExpand(!expanded)}
          >
            {expanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </button>
          <button type="button" aria-label="Close details" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      )}
      <div className="rm-detail-copy" aria-live="polite">
        <span className="rm-eyebrow">{subtitle}</span>
        <h2>{title}</h2>
        <p>{description}</p>
        <div className="rm-progress-caption">
          <span>{progressText}</span>
          {progress >= 100 && <Check size={15} aria-label="Complete" />}
        </div>
        <div
          className="rm-progress-track"
          role="progressbar"
          aria-label={progressText}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <i style={{ width: `${progress}%` }} />
        </div>
      </div>
      {(src || stage.levelNumber !== 0 || !isLesson) && (
        <ScorePreview key={src} src={src} title={title} />
      )}
      <div className="rm-detail-actions">
        <button
          type="button"
          className={`rm-play ${!isLesson && !isPiece ? 'is-repertoire' : ''}`}
          aria-label={
            isLesson
              ? 'Play selected roadmap level'
              : isPiece
                ? `Play ${piece.piece.name}`
                : `Open Level ${stage.levelNumber} repertoire`
          }
          onClick={!isLesson && !isPiece ? onBrowse : onPlay}
        >
          {!isLesson && !isPiece ? <BookOpen size={25} /> : <Play size={27} fill="currentColor" />}
          <span>{!isLesson && !isPiece ? 'Open repertoire' : 'PLAY'}</span>
        </button>
        {isLesson && stage.levelNumber > 0 && stage.highestTierComplete > 0 && (
          <button className="rm-text-button" onClick={onReplay}>
            <RotateCcw size={14} />
            Replay tutorial
          </button>
        )}
        {isPiece && (
          <button className="rm-text-button" onClick={onBrowse}>
            View level repertoire
          </button>
        )}
      </div>
    </section>
  );
}
